import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { Feature } from "@omni/types/api/product/bff/shared/feature.model";
import { ImageViewerImage } from "@omni/types/api/product/bff/shared/imageViewerImage.model";
import { ImageViewerVideo } from "@omni/types/api/product/bff/shared/imageViewerVideo.model";
import { Review } from "@omni/types/api/reviews/bff/response/review.model";
import { ReviewsSummary } from "@omni/types/api/reviews/bff/response/reviewsSummary.model";
import { AlgoliaRecommendParams } from "@omni/types/api/search/algolia/recommend/algoliaRecommendParams.model";
import { ECommerceTooltips } from "@omni/types/components/product/eCommerce/eCommerce.type";
import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { ProductPageInfoCards } from "@omni/types/components/product/productPage/infoCards.type";
import {
    Usp,
    Usps
} from "@omni/types/components/product/productPage/usps.type";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use } from "react";

import { RecoverabilityIndex } from "@/components/RecoverabilityIndex/recoverabilityIndex.types";

import {
    EnergyLabelAdapter,
    EnergyLabelAdapterType
} from "../components/adapters/energyLabel.adapter";
import { ImageViewerAdapter } from "../components/adapters/imageViewer.adapter";
import { RecoverabilityIndexAdapter } from "../components/adapters/recoverabilityIndex.adapter";

type ProductPageContextValue = {
    algoliaRecommendParams: AlgoliaRecommendParams;
    brandPagePath?: string;
    energyLabel?: EnergyLabelAdapterType;
    features: Array<Feature>;
    images: Array<ImageViewerImage>;
    infoCards: ProductPageInfoCards;
    infoLink?: string;
    maxVisibleFeatures: number;
    product: Product<ProductFieldSet.Full>;
    promoBannerHTML?: string;
    recoverabilityIndex: RecoverabilityIndex | null;
    reviews?: Array<Review>;
    reviewsSummary?: ReviewsSummary;
    sliders?: Partial<
        Record<
            ProductSliderSources,
            Array<Product<ProductFieldSet.ProductTile>>
        >
    >;
    tooltips: ECommerceTooltips;
    totalReviews?: number;
    usesDefaultImage: "0" | "1";
    videos: Array<ImageViewerVideo>;
    cashbackPagePath: string;
    contextAwareXfPath: string | null;
    paintConfiguratorPath: string;
    isEcochequesAccepted: boolean;
    isClickAndCollectAvailable: boolean;
    isExclusivelyOnline: boolean;
    isWebOnly: boolean;
    usps: Usps;
};

export type ProductPageContextProviderProps = {
    algoliaRecommendParams: AlgoliaRecommendParams;
    brandPagePath?: string;
    cashbackPagePath: string;
    contextAwareXfPath: string | null;
    paintConfiguratorPath: string;
    infoCards: ProductPageInfoCards;
    infoLink?: string;
    maxVisibleFeatures: number;
    product: Product<ProductFieldSet.Full>;
    promoBannerHTML?: string;
    reviews?: Array<Review>;
    reviewsSummary?: ReviewsSummary;
    sliders?: Partial<
        Record<
            ProductSliderSources,
            Array<Product<ProductFieldSet.ProductTile>>
        >
    >;
    tooltips: ECommerceTooltips;
    totalReviews?: number;
    usps: Array<Usp> | null;
};

export const ProductPageContext = createContext<
    ProductPageContextValue | undefined
>(undefined);

ProductPageContext.displayName = "ProductPageContext";

export const ProductPageContextProvider = ({
    algoliaRecommendParams,
    brandPagePath,
    cashbackPagePath,
    children,
    contextAwareXfPath,
    paintConfiguratorPath,
    infoCards,
    infoLink,
    maxVisibleFeatures,
    product,
    promoBannerHTML,
    reviews,
    reviewsSummary,
    sliders,
    tooltips,
    totalReviews,
    usps
}: RequireChildrenFor<ProductPageContextProviderProps>) => {
    const { images, videos, usesDefaultImage } = ImageViewerAdapter(product);

    const recoverabilityIndex = RecoverabilityIndexAdapter(product);

    const energyLabel = EnergyLabelAdapter(product);

    const visibilityType = (product?.productVisibilityType ||
        ProductVisibilityType.Default) as ProductVisibilityType;

    const isClickAndCollectAvailable = (
        visibilityType: ProductVisibilityType | string
    ) => {
        switch (visibilityType) {
            case ProductVisibilityType.Collect:
            case ProductVisibilityType.DeliveryAndCollect:
            case ProductVisibilityType.CollectAndExpressDelivery:
            case ProductVisibilityType.DeliveryAndExpressDeliveryAndCollect:
                return true;
            default:
                return false;
        }
    };

    const isHomeDeliveryAvailable = (
        visibilityType: ProductVisibilityType | string
    ) => {
        switch (visibilityType) {
            case ProductVisibilityType.Delivery:
            case ProductVisibilityType.DeliveryAndCollect:
            case ProductVisibilityType.DeliveryAndExpressDeliveryAndCollect:
                return true;
            default:
                return false;
        }
    };

    return (
        <ProductPageContext
            value={{
                algoliaRecommendParams,
                brandPagePath,
                energyLabel,
                features: product.features || [],
                images,
                infoCards,
                infoLink,
                maxVisibleFeatures,
                product,
                promoBannerHTML,
                recoverabilityIndex,
                reviews,
                reviewsSummary,
                sliders,
                tooltips,
                totalReviews,
                usesDefaultImage,
                videos,
                cashbackPagePath,
                contextAwareXfPath,
                paintConfiguratorPath,
                usps,
                isEcochequesAccepted: !!product.ecochequesAccepted,
                isClickAndCollectAvailable:
                    isClickAndCollectAvailable(visibilityType),
                isExclusivelyOnline:
                    isHomeDeliveryAvailable(visibilityType) &&
                    !isClickAndCollectAvailable(visibilityType),
                isWebOnly: !!product.webOnly
            }}
        >
            {children}
        </ProductPageContext>
    );
};

export const useProductPageContext = (): ProductPageContextValue => {
    const context = use<ProductPageContextValue | undefined>(
        ProductPageContext
    );
    if (!isDefined(context)) {
        throw new Error(`${ProductPageContext.displayName} is undefined.`);
    }
    return context;
};
